import { useEffect, useRef, useState } from 'react';
import { Logo } from '@sab/slides';

interface TopbarProps {
  userEmail: string | null;
  env: string;
  onHome: () => void;
}

export function Topbar({ userEmail, env, onHome }: TopbarProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const initials = userEmail
    ? (userEmail
        .split('@')[0]
        ?.split(/[._-]/)
        .map((s) => s[0]?.toUpperCase() ?? '')
        .join('')
        .slice(0, 2) ?? '·')
    : '·';

  return (
    <header className="topbar" role="banner">
      <button className="topbar__brand" onClick={onHome} aria-label="Beranda" type="button">
        <Logo variant="navy" />
      </button>
      <div className="topbar__spacer" />
      {env !== 'production' ? (
        <span className="topbar__env" aria-label={`Environment: ${env}`}>
          {env}
        </span>
      ) : null}
      <div className="topbar__user" ref={menuRef}>
        <button
          className="avatar"
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={userEmail ? `Akun ${userEmail}` : 'Akun'}
          onClick={() => setOpen((o) => !o)}
          type="button"
        >
          <span aria-hidden="true">{initials}</span>
        </button>
        {open ? (
          <div className="menu" role="menu">
            <div className="menu__head">
              <p className="menu__label">Masuk sebagai</p>
              <p className="menu__email">{userEmail ?? '—'}</p>
            </div>
            <div className="menu__form">
              <a
                href="#/settings"
                className="menu__item"
                role="menuitem"
                onClick={() => setOpen(false)}
              >
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
                  <path
                    d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinejoin="round"
                  />
                </svg>
                Pengaturan
              </a>
              <div className="menu__sep" role="separator" />
            </div>
            <form
              method="POST"
              action="/proposal/api/auth/logout"
              className="menu__form"
              role="none"
            >
              <button className="menu__item menu__item--danger" type="submit" role="menuitem">
                <svg aria-hidden="true" width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M15 17l5-5-5-5M20 12H9M12 19a7 7 0 1 1 0-14"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                Keluar
              </button>
            </form>
          </div>
        ) : null}
      </div>
    </header>
  );
}
